import React, { useState } from 'react';

interface IToastProvider {
	show: boolean;
	type: Types;
	text: string;
	showToast: (type: Types, text?: string, timeout?: number) => void;
	closeToast: () => void;
}

type Types = 'success' | 'error';

const ToastContext = React.createContext<IToastProvider>(undefined!);

/**
 * React context provider that manages global toast notification state.
 *
 * Wrap the application root with `<ToastContextProvider>` and call
 * `useToastContext().showToast` from any component to trigger a toast.
 *
 * @example
 * // In App.tsx
 * <ToastContextProvider><App /></ToastContextProvider>
 *
 * // In any component
 * const { showToast } = useToastContext();
 * showToast('success', 'Saved successfully!');
 * showToast('error', 'Something went wrong.', 5000);
 */
export function ToastContextProvider({ children }: { children: React.ReactNode }): JSX.Element {
	const { show, showToast, closeToast, type, text } = useProviderFunctions();
	return <ToastContext.Provider value={{ show, showToast, closeToast, type, text }}>{children}</ToastContext.Provider>;
}

export function useProviderFunctions() {
	const [show, setShow] = useState<boolean>(false);
	const [type, setType] = useState<Types>('success');
	const [text, setText] = useState<string>('Something went wrong!');

	const showToast = (type: Types, text?: string, timeout?: number) => {
		setShow(true);
		setType(type);
		if (text) {
			setText(text);
		}
		setTimeout(() => {
			setShow(false);
		}, timeout || 3000);
	};

	const closeToast = () => {
		setShow(false);
	};

	return {
		show,
		showToast,
		closeToast,
		type,
		text,
	};
}

/**
 * Returns the current toast context value.
 * Must be used inside a {@link ToastContextProvider}.
 * @throws If used outside the provider, returns `undefined`.
 */
export const useToastContext = (): IToastProvider => React.useContext(ToastContext);
